/**
 * Kacheln auf „Mein Team": Ampelverteilung („Risikolage"), Ø Auslastung und
 * „Auffällig". Die Risikolage folgt seit dem 16.09.2026 dem Schalter Bindung /
 * Wirtschaftlichkeit (Marie: Variante „C") — deshalb beide Verteilungen.
 *
 * Bis zum 16.09.2026 rechnete die Seite das in der Komponente selbst. Hier
 * stehen die Regeln prüfbar — und dieselben wie in der Übersicht der
 * Wirtschaftlichkeit (src/services/wirtschaftlichkeit-aggregat.ts): Die
 * Ø Auslastung mittelt nur über Mitarbeiter, deren Auslastung die Ampel
 * bewertet; die Ampelverteilung zählt nur Mitarbeiter mit bewerteter Ampel
 * (Marie: „nicht bewertete sollen gar nicht zählen").
 *
 * Bewusst ohne React — die Unit-Tests laufen auf GitHub vor der UI-Installation.
 */

export type AmpelStufe = "ok" | "warning" | "critical";

export interface TeamKennzahlZeile {
  wirtschaft: {
    level: AmpelStufe;
    utilizationAvg3m: number | null;
    auslastungBewertet: boolean;
    ampelBewertet: boolean;
  };
  bindung: { level: AmpelStufe; isConfirmedLeaving: boolean };
}

export interface TeamKennzahlen {
  /** Wirtschafts-Ampel — nur Mitarbeiter mit bewerteter Ampel. */
  wirtschaftAmpel: Record<AmpelStufe, number>;
  /**
   * Bindungs-Ampel — alle Mitarbeiter außer bestätigten Kündigungen: Die sind
   * kein Risikofall mehr (Prinzip Marie 11.06.2026), aber auch nicht „stabil".
   */
  bindungAmpel: Record<AmpelStufe, number>;
  utilizationAvg: number | null;
  auffaellig: number;
}

export function berechneTeamKennzahlen(rows: readonly TeamKennzahlZeile[]): TeamKennzahlen {
  const wirtschaftAmpel: Record<AmpelStufe, number> = { ok: 0, warning: 0, critical: 0 };
  const bindungAmpel: Record<AmpelStufe, number> = { ok: 0, warning: 0, critical: 0 };
  let utilSum = 0;
  let utilCount = 0;
  let auffaellig = 0;
  for (const r of rows) {
    if (r.wirtschaft.ampelBewertet) wirtschaftAmpel[r.wirtschaft.level] += 1;
    if (!r.bindung.isConfirmedLeaving) bindungAmpel[r.bindung.level] += 1;
    if (r.wirtschaft.auslastungBewertet && r.wirtschaft.utilizationAvg3m != null) {
      utilSum += r.wirtschaft.utilizationAvg3m;
      utilCount += 1;
    }
    // Bindung zählt nicht mehr, wenn die Kündigung bestätigt ist (analog rowSeverity)
    const bindungNichtGruen = !r.bindung.isConfirmedLeaving && r.bindung.level !== "ok";
    if (bindungNichtGruen || r.wirtschaft.level !== "ok") auffaellig += 1;
  }
  return {
    wirtschaftAmpel,
    bindungAmpel,
    utilizationAvg: utilCount > 0 ? Math.round(utilSum / utilCount) : null,
    auffaellig,
  };
}
